import { motion } from "framer-motion";
import { CARD, BD, TX, TX2, GR, BL, YL, RD } from "../../utils/constants";
import { biasColor, scoreColor } from "../../utils/formatUtils";

export default function PredictTab({ predLog }) {
  const resolved = predLog.filter((p) => p.status !== "pending");
  const pending  = predLog.filter((p) => p.status === "pending");
  const wins     = resolved.filter((p) => p.status === "win").length;
  const acc      = resolved.length > 0 ? ((wins / resolved.length) * 100).toFixed(0) : null;

  return (
    <div>
      {/* Page header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
        style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 900, fontSize: 22, letterSpacing: 1, background: `linear-gradient(90deg, ${BL}, ${GR})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          PREDICT
        </div>
        <div style={{ fontSize: 8, color: TX2, letterSpacing: 3, marginTop: 4 }}>SIGNAL ACCURACY TRACKER · 5-DAY RESOLUTION</div>
        <div style={{ height: 1, background: `linear-gradient(90deg, ${BL}40, transparent)`, marginTop: 8 }} />
      </motion.div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 9, marginBottom: 12 }}>
        {[
          { l: "PREDICTIONS", v: resolved.length, c: TX },
          { l: "WINS",        v: wins,             c: GR },
          { l: "LOSSES",      v: resolved.filter((p) => p.status === "loss").length, c: RD },
          { l: "ACCURACY",    v: acc ? acc + "%" : "—", c: acc >= 55 ? GR : acc >= 45 ? YL : acc ? RD : TX2 },
        ].map((s, i) => (
          <motion.div key={s.l} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.07, ease: "easeOut" }}
            className="ts-panel"
            style={{ background: CARD, border: `1px solid ${BD}`, borderRadius: 9, padding: "12px 14px" }}>
            <div style={{ fontSize: 7, color: TX2, letterSpacing: 2, marginBottom: 4 }}>{s.l}</div>
            <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Space Grotesk',sans-serif", color: s.c }}>{s.v}</div>
          </motion.div>
        ))}
      </div>

      {/* Active predictions */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.3 }}
        className="ts-panel"
        style={{ background: CARD, border: `1px solid ${BD}`, borderRadius: 11, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ padding: "10px 14px", borderBottom: `1px solid ${BD}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 8, color: TX2, letterSpacing: 2 }}>ACTIVE PREDICTIONS — resolves 5 days after scan</span>
          {pending.length > 0 && <span style={{ fontSize: 8, color: TX2 }}>{pending.length} pending</span>}
        </div>
        {pending.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: TX2, fontSize: 9 }}>Run a scan to start tracking BULLISH / BEARISH predictions automatically</div>
        ) : [...pending].reverse().slice(0, 20).map((p, i) => (
          <motion.div key={p.id}
            initial={{ opacity: 0, x: -14 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2, delay: Math.min(i * 0.04, 0.35), ease: "easeOut" }}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", borderBottom: `1px solid ${BD}`, borderLeft: `3px solid ${biasColor(p.bias)}` }}>
            <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: 12, color: TX, minWidth: 56 }}>{p.symbol}</div>
            <div style={{ fontSize: 8, fontWeight: 700, color: biasColor(p.bias), minWidth: 64 }}>{p.bias}</div>
            <div style={{ fontSize: 9, color: TX, minWidth: 56 }}>${p.price?.toFixed(2)}</div>
            <div style={{ fontSize: 9, color: scoreColor(p.score), fontWeight: 700, minWidth: 28 }}>{p.score}</div>
            <div style={{ fontSize: 8, color: TX2, flex: 1, textAlign: "right" }}>{p.date}</div>
          </motion.div>
        ))}
      </motion.div>

      {/* Resolved predictions */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.42 }}
        className="ts-panel"
        style={{ background: CARD, border: `1px solid ${BD}`, borderRadius: 11, overflow: "hidden" }}>
        <div style={{ padding: "10px 14px", borderBottom: `1px solid ${BD}`, fontSize: 8, color: TX2, letterSpacing: 2 }}>RESOLVED PREDICTIONS</div>
        {resolved.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: TX2, fontSize: 9 }}>Predictions resolve on next scan after 5+ days have passed</div>
        ) : [...resolved].reverse().slice(0, 30).map((p, i) => (
          <motion.div key={p.id}
            initial={{ opacity: 0, x: -14 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2, delay: Math.min(i * 0.035, 0.35), ease: "easeOut" }}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", borderBottom: `1px solid ${BD}`, borderLeft: `3px solid ${p.status === "win" ? GR : RD}` }}>
            <div style={{ fontSize: 8, fontWeight: 700, color: p.status === "win" ? GR : RD, background: p.status === "win" ? GR + "12" : RD + "12", borderRadius: 3, padding: "2px 5px", minWidth: 36, textAlign: "center" }}>{p.status.toUpperCase()}</div>
            <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: 12, color: TX, minWidth: 56 }}>{p.symbol}</div>
            <div style={{ fontSize: 8, fontWeight: 700, color: biasColor(p.bias), minWidth: 64 }}>{p.bias}</div>
            <div style={{ fontSize: 9, color: TX2 }}>${p.price?.toFixed(2)} → <span style={{ color: TX }}>${p.resolvedPrice?.toFixed(2)}</span></div>
            <div style={{ fontSize: 10, fontWeight: 700, color: (p.returnPct || 0) >= 0 ? GR : RD, flex: 1, textAlign: "right" }}>{(p.returnPct || 0) >= 0 ? "+" : ""}{p.returnPct?.toFixed(1)}%</div>
            <div style={{ fontSize: 8, color: TX2 }}>{p.date}</div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}
